import React, { useRef, useEffect, useState, useCallback } from 'react';
import * as d3 from 'd3';

const SVG_HEIGHT = 400;

const styles = {
  card: {
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    padding: '24px',
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
  title: {
    fontSize: '16px',
    fontWeight: 600,
    color: '#1f2937',
    marginBottom: '4px',
  },
  subtitle: {
    fontSize: '12px',
    color: '#9ca3af',
    marginBottom: '16px',
  },
  controls: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    marginBottom: '12px',
    flexWrap: 'wrap',
  },
  sliderWrapper: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '12px',
    color: '#6b7280',
  },
  sliderLabel: {
    fontSize: '12px',
    fontWeight: 500,
    color: '#374151',
    minWidth: '120px',
  },
  slider: {
    width: '180px',
    cursor: 'pointer',
    accentColor: '#3b82f6',
  },
  sliderValue: {
    fontSize: '12px',
    fontWeight: 600,
    color: '#1f2937',
    minWidth: '32px',
    textAlign: 'right',
  },
  container: {
    overflowX: 'auto',
    position: 'relative',
  },
  tooltip: {
    position: 'fixed',
    pointerEvents: 'none',
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '6px',
    padding: '8px 12px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
    fontSize: '12px',
    zIndex: 1000,
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    maxWidth: '220px',
  },
  legend: {
    display: 'flex',
    gap: '16px',
    marginTop: '12px',
    fontSize: '11px',
    color: '#9ca3af',
    flexWrap: 'wrap',
  },
  legendItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
  },
  legendSwatch: {
    display: 'inline-block',
    width: '12px',
    height: '12px',
    borderRadius: '2px',
    verticalAlign: 'middle',
  },
  legendLine: {
    display: 'inline-block',
    width: '20px',
    height: '3px',
    borderRadius: '1px',
    verticalAlign: 'middle',
  },
};

/**
 * Maps an average price to a color on a green -> blue -> red gradient.
 * Low prices = green, mid prices = blue, high prices = red.
 */
function priceColor(avgPrice, priceExtent) {
  const [minP, maxP] = priceExtent;
  if (maxP === minP) return '#3b82f6';
  const t = (avgPrice - minP) / (maxP - minP);
  if (t <= 0.5) {
    // green (#22c55e) to blue (#3b82f6)
    const s = t / 0.5;
    const r = Math.round(34 + (59 - 34) * s);
    const g = Math.round(197 + (130 - 197) * s);
    const b = Math.round(94 + (246 - 94) * s);
    return `rgb(${r},${g},${b})`;
  }
  // blue (#3b82f6) to red (#ef4444)
  const s = (t - 0.5) / 0.5;
  const r = Math.round(59 + (239 - 59) * s);
  const g = Math.round(130 + (68 - 130) * s);
  const b = Math.round(246 + (68 - 246) * s);
  return `rgb(${r},${g},${b})`;
}

/**
 * Scale addressCount to a node radius between MIN_RADIUS and MAX_RADIUS.
 */
const MIN_RADIUS = 10;
const MAX_RADIUS = 30;

function nodeRadius(addressCount, countExtent) {
  const [minC, maxC] = countExtent;
  if (maxC === minC) return (MIN_RADIUS + MAX_RADIUS) / 2;
  const t = (addressCount - minC) / (maxC - minC);
  return MIN_RADIUS + t * (MAX_RADIUS - MIN_RADIUS);
}

export default function NetworkGraph({ data }) {
  const svgRef = useRef(null);
  const containerRef = useRef(null);
  const simulationRef = useRef(null);
  const [tooltip, setTooltip] = useState(null);
  const [threshold, setThreshold] = useState(null);

  // Initialize threshold from data when it first arrives
  useEffect(() => {
    if (data && data.threshold != null && threshold === null) {
      setThreshold(data.threshold);
    }
  }, [data, threshold]);

  const activeThreshold = threshold != null ? threshold : (data && data.threshold != null ? data.threshold : 0.5);

  const handleThresholdChange = useCallback((e) => {
    setThreshold(parseFloat(e.target.value));
  }, []);

  useEffect(() => {
    if (!data || !data.nodes || data.nodes.length === 0 || !data.links) return;

    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    // Stop any previous simulation
    if (simulationRef.current) {
      simulationRef.current.stop();
      simulationRef.current = null;
    }

    const { nodes: rawNodes, links: rawLinks } = data;

    // Filter links by threshold
    const filteredLinks = rawLinks.filter((l) => Math.abs(l.correlation) >= activeThreshold);

    // Deep-copy nodes so D3 mutation doesn't affect props
    const nodes = rawNodes.map((n) => ({ ...n }));
    const links = filteredLinks.map((l) => ({ ...l }));

    // Compute extents
    const countExtent = d3.extent(nodes, (n) => n.addressCount);
    const priceExtent = d3.extent(nodes, (n) => n.avgPrice);

    // Sizing
    const containerWidth = containerRef.current
      ? containerRef.current.clientWidth
      : 700;
    const width = Math.max(500, containerWidth);
    const height = SVG_HEIGHT;

    svg
      .attr('width', '100%')
      .attr('height', height)
      .attr('viewBox', `0 0 ${width} ${height}`)
      .attr('preserveAspectRatio', 'xMidYMid meet');

    const g = svg.append('g');

    // Build the force simulation
    const simulation = d3.forceSimulation(nodes)
      .force('center', d3.forceCenter(width / 2, height / 2))
      .force('charge', d3.forceManyBody().strength(-200))
      .force('link', d3.forceLink(links)
        .id((d) => d.id)
        .distance(120)
        .strength((d) => Math.abs(d.correlation) * 0.5)
      )
      .force('collide', d3.forceCollide().radius((d) => nodeRadius(d.addressCount, countExtent) + 4));

    simulationRef.current = simulation;

    // Draw edges
    const linkSelection = g.selectAll('.link')
      .data(links)
      .enter()
      .append('line')
      .attr('class', 'link')
      .attr('stroke', (d) => d.correlation >= 0 ? '#3b82f6' : '#ef4444')
      .attr('stroke-width', (d) => 1 + Math.abs(d.correlation) * 3)
      .attr('stroke-opacity', (d) => 0.2 + Math.abs(d.correlation) * 0.6);

    // Draw node groups
    const nodeGroup = g.selectAll('.node')
      .data(nodes)
      .enter()
      .append('g')
      .attr('class', 'node')
      .style('cursor', 'grab')
      .call(d3.drag()
        .on('start', function (event, d) {
          if (!event.active) simulation.alphaTarget(0.3).restart();
          d.fx = d.x;
          d.fy = d.y;
          d3.select(this).style('cursor', 'grabbing');
        })
        .on('drag', function (event, d) {
          d.fx = event.x;
          d.fy = event.y;
        })
        .on('end', function (event, d) {
          if (!event.active) simulation.alphaTarget(0);
          d.fx = null;
          d.fy = null;
          d3.select(this).style('cursor', 'grab');
        })
      );

    // Node circles
    nodeGroup.append('circle')
      .attr('r', (d) => nodeRadius(d.addressCount, countExtent))
      .attr('fill', (d) => priceColor(d.avgPrice, priceExtent))
      .attr('stroke', '#fff')
      .attr('stroke-width', 2)
      .attr('opacity', 0.9);

    // Node labels (state abbreviation)
    nodeGroup.append('text')
      .text((d) => d.id)
      .attr('text-anchor', 'middle')
      .attr('dominant-baseline', 'central')
      .attr('font-size', '11px')
      .attr('font-weight', 600)
      .attr('fill', '#fff')
      .attr('pointer-events', 'none');

    // Hover interaction
    nodeGroup
      .on('mouseenter', function (event, d) {
        d3.select(this).select('circle')
          .attr('stroke', '#1f2937')
          .attr('stroke-width', 3)
          .attr('opacity', 1);

        // Find top 3 correlated states for this node
        const connectedLinks = rawLinks
          .filter((l) => l.source === d.id || l.target === d.id)
          .map((l) => ({
            state: l.source === d.id ? l.target : l.source,
            correlation: l.correlation,
          }))
          .sort((a, b) => Math.abs(b.correlation) - Math.abs(a.correlation))
          .slice(0, 3);

        setTooltip({
          x: event.clientX + 14,
          y: event.clientY - 14,
          state: d.label,
          stateId: d.id,
          addressCount: d.addressCount,
          avgPrice: d.avgPrice,
          topCorrelated: connectedLinks,
        });
      })
      .on('mousemove', function (event) {
        setTooltip((prev) => prev ? { ...prev, x: event.clientX + 14, y: event.clientY - 14 } : prev);
      })
      .on('mouseleave', function () {
        d3.select(this).select('circle')
          .attr('stroke', '#fff')
          .attr('stroke-width', 2)
          .attr('opacity', 0.9);
        setTooltip(null);
      });

    // Tick handler
    simulation.on('tick', () => {
      // Constrain nodes within SVG bounds
      nodes.forEach((d) => {
        const r = nodeRadius(d.addressCount, countExtent);
        d.x = Math.max(r, Math.min(width - r, d.x));
        d.y = Math.max(r, Math.min(height - r, d.y));
      });

      linkSelection
        .attr('x1', (d) => d.source.x)
        .attr('y1', (d) => d.source.y)
        .attr('x2', (d) => d.target.x)
        .attr('y2', (d) => d.target.y);

      nodeGroup.attr('transform', (d) => `translate(${d.x},${d.y})`);
    });

    return () => {
      simulation.stop();
      simulationRef.current = null;
      svg.selectAll('*').remove();
    };
  }, [data, activeThreshold]);

  // Empty state
  if (!data || !data.nodes || data.nodes.length === 0) {
    return (
      <div style={styles.card}>
        <div style={styles.title}>State Price Correlation Network</div>
        <div style={styles.subtitle}>Connected states show correlated pest365 price movements</div>
        <div style={{ color: '#6b7280', textAlign: 'center', padding: '40px 0' }}>
          No correlation network data available
        </div>
      </div>
    );
  }

  const visibleLinkCount = data.links
    ? data.links.filter((l) => Math.abs(l.correlation) >= activeThreshold).length
    : 0;

  return (
    <div style={styles.card}>
      <div style={styles.title}>State Price Correlation Network</div>
      <div style={styles.subtitle}>Connected states show correlated pest365 price movements</div>

      <div style={styles.controls}>
        <div style={styles.sliderWrapper}>
          <span style={styles.sliderLabel}>Correlation threshold:</span>
          <input
            type="range"
            min="0.3"
            max="0.9"
            step="0.05"
            value={activeThreshold}
            onChange={handleThresholdChange}
            style={styles.slider}
          />
          <span style={styles.sliderValue}>{activeThreshold.toFixed(2)}</span>
        </div>
        <span style={{ fontSize: '11px', color: '#9ca3af' }}>
          {visibleLinkCount} connection{visibleLinkCount !== 1 ? 's' : ''} shown
        </span>
      </div>

      <div style={styles.container} ref={containerRef}>
        <svg ref={svgRef} />
      </div>

      {tooltip && (
        <div
          style={{
            ...styles.tooltip,
            left: tooltip.x,
            top: tooltip.y,
          }}
        >
          <div style={{ fontWeight: 600, color: '#1f2937', marginBottom: '4px' }}>
            {tooltip.state} ({tooltip.stateId})
          </div>
          <div style={{ color: '#6b7280', marginBottom: '2px' }}>
            Addresses: {tooltip.addressCount}
          </div>
          <div style={{ color: '#2563eb', fontWeight: 600, marginBottom: '4px' }}>
            Avg pest365: ${tooltip.avgPrice.toFixed(2)}
          </div>
          {tooltip.topCorrelated.length > 0 && (
            <div style={{ borderTop: '1px solid #e5e7eb', paddingTop: '4px', marginTop: '2px' }}>
              <div style={{ fontSize: '10px', color: '#9ca3af', marginBottom: '2px' }}>Top correlated:</div>
              {tooltip.topCorrelated.map((c) => (
                <div key={c.state} style={{ fontSize: '11px', color: '#374151' }}>
                  {c.state}: <span style={{ fontWeight: 600, color: c.correlation >= 0 ? '#2563eb' : '#ef4444' }}>
                    r={c.correlation.toFixed(3)}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <div style={styles.legend}>
        <div style={styles.legendItem}>
          <span style={{ ...styles.legendLine, background: '#3b82f6' }} />
          <span>Positive correlation</span>
        </div>
        <div style={styles.legendItem}>
          <span style={{ ...styles.legendLine, background: '#ef4444' }} />
          <span>Negative correlation</span>
        </div>
        <div style={styles.legendItem}>
          <span style={{ ...styles.legendSwatch, background: '#22c55e', borderRadius: '50%' }} />
          <span>Low price</span>
        </div>
        <div style={styles.legendItem}>
          <span style={{ ...styles.legendSwatch, background: '#3b82f6', borderRadius: '50%' }} />
          <span>Mid price</span>
        </div>
        <div style={styles.legendItem}>
          <span style={{ ...styles.legendSwatch, background: '#ef4444', borderRadius: '50%' }} />
          <span>High price</span>
        </div>
        <div style={styles.legendItem}>
          <span>Node size = address count</span>
        </div>
      </div>
    </div>
  );
}
